/**
 * Browser/node DOCX fill via fflate. Wire bytes are copied, never merged.
 */
import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { emptyRaisePackageFields, type RaisePackageFields } from "./raise-package-fields.js";

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export class UnresolvedRaisePackageTokenError extends Error {
  constructor(message = "Raise package template still contains unresolved placeholders.") {
    super(message);
    this.name = "UnresolvedRaisePackageTokenError";
  }
}

export function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

export function fillXmlPlaceholders(xml: string, fields: RaisePackageFields): string {
  const known = new Set(Object.keys(fields));
  return xml.replace(TOKEN_RE, (full, name: string) => {
    if (!known.has(name)) return full;
    return escapeXml(fields[name as keyof RaisePackageFields] ?? "");
  });
}

function isXmlPart(name: string): boolean {
  return name.toLowerCase().endsWith(".xml");
}

export function fillDocxTemplate(bytes: Uint8Array, fields: RaisePackageFields): Uint8Array {
  const map = { ...emptyRaisePackageFields(), ...fields };
  const unzipped = unzipSync(bytes);
  for (const [name, part] of Object.entries(unzipped)) {
    if (!part || !isXmlPart(name)) continue;
    const xml = strFromU8(part);
    if (!xml.includes("{{")) continue;
    unzipped[name] = strToU8(fillXmlPlaceholders(xml, map));
  }
  for (const [name, part] of Object.entries(unzipped)) {
    if (!part || !isXmlPart(name)) continue;
    if (strFromU8(part).includes("{{")) {
      throw new UnresolvedRaisePackageTokenError(`Unresolved placeholder in ${name}`);
    }
  }
  return zipSync(unzipped);
}

export function copyWireDocument(bytes: Uint8Array): Uint8Array {
  return new Uint8Array(bytes);
}

export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
